// src/games/darkforest/scenes/forest_path.js
import { DIFFICULTY_CLASS } from '../../../game-system/skills/skillSystem';

export const forest_path = {
  forest_path_fork: {
    id: 'forest_path_fork',
    title: "Forest Path Fork",
    description: `After walking for about half an hour, you reach a fork in the path. The forest has grown denser, with towering trees blocking most of the sunlight. The air is cool and damp, filled with the earthy scent of moss and decaying leaves.

To the left, the path descends slightly and appears to lead toward a sound of running water.

To the right, the path climbs gently up a slope and seems to be less traveled, with branches and undergrowth partially reclaiming it.

Straight ahead, the main path continues deeper into the forest, disappearing into the shadows.`,
    choices: [
      {
        id: 'take_left_path',
        text: "Take the left path toward the water",
        nextScene: 'stream_approach',
        requires: null
      },
      {
        id: 'take_right_path',
        text: "Take the right path up the slope",
        nextScene: 'forest_ridge',
        requires: null
      },
      {
        id: 'continue_straight',
        text: "Continue straight ahead on the main path",
        nextScene: 'dense_forest',
        requires: null
      },
      {
        id: 'examine_surroundings',
        text: "Examine the surroundings for clues",
        nextScene: 'fork_examination',
        requires: null
      }
    ]
  },
  
  fork_examination: {
    id: 'fork_examination',
    title: "Examining the Fork",
    description: `You pause to carefully examine the area around the fork in the path, looking for any signs that might guide your decision.`,
    skillChecks: [
      {
        skill: 'investigation',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Search for clues about which path to take",
        results: {
          success: "Near the right path, you find a broken arrow shaft with unusual fletching. On the main path, you notice occasional boot prints heading inward. The left path shows signs of animal tracks leading to water.",
          failure: "Nothing particularly unusual catches your attention at the fork."
        }
      },
      {
        skill: 'survival',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Read the natural signs in the area",
        results: {
          success: "The right path shows fewer animal tracks, suggesting possible danger or unsuitable habitat. The main path and left path both show regular use by forest creatures, suggesting they're relatively safe routes.",
          failure: "The usual forest signs are present, but you can't determine anything specific about the different paths."
        }
      }
    ],
    choices: [
      {
        id: 'take_left_path',
        text: "Take the left path toward the water",
        nextScene: 'stream_approach',
        requires: null
      },
      {
        id: 'take_right_path',
        text: "Take the right path up the slope",
        nextScene: 'forest_ridge',
        requires: null
      },
      {
        id: 'continue_straight',
        text: "Continue straight ahead on the main path",
        nextScene: 'dense_forest',
        requires: null
      }
    ]
  },
  
  dense_forest: {
    id: 'dense_forest',
    title: "Into the Dense Forest",
    description: `You continue along the main path as it winds deeper into the forest. The trees grow larger and stand closer together, their massive trunks covered in moss and lichen. The canopy above is so thick that only occasional shafts of sunlight penetrate to the forest floor.

The path narrows further, becoming little more than a game trail in places. The underbrush grows denser on either side, and strange mushrooms glow with a faint blue light in the shadows.

As you walk, you begin to feel as though you're being watched. Occasionally, you catch glimpses of movement at the edge of your vision, but when you turn to look, nothing is there.`,
    choices: [
      {
        id: 'continue_forward',
        text: "Continue deeper into the forest",
        nextScene: 'mysterious_clearing',
        requires: null
      },
      {
        id: 'investigate_mushrooms',
        text: "Investigate the glowing mushrooms",
        nextScene: 'glowing_mushrooms',
        requires: null
      },
      {
        id: 'stop_listen',
        text: "Stop and listen carefully",
        nextScene: 'forest_sounds',
        requires: null
      }
    ],
    encounters: ['poisonous_mushrooms', 'animal_tracks']
  },
  
  glowing_mushrooms: {
    id: 'glowing_mushrooms',
    title: "Glowing Mushrooms",
    description: `You kneel down to examine the strange, luminescent mushrooms growing along the path. They emit a soft blue glow that becomes more noticeable as you look at them. The caps are small and bell-shaped, growing in clusters around the bases of the oldest trees.`,
    skillChecks: [
      {
        skill: 'nature',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Identify the mushroom species",
        results: {
          success: "You recognize these as 'Sylvan Lanterns,' a rare type of mushroom rumored to grow only in forests with strong magical properties. They're said to absorb magical energy from their surroundings, which causes the glow. While not edible, they can be used in certain alchemical preparations.",
          rewards: {
            experience: 35
          },
          failure: "These mushrooms are unlike any you've seen before. Without more knowledge, you can't determine if they're useful, harmless, or dangerous."
        }
      },
      {
        skill: 'arcana',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Sense any magical properties",
        results: {
          success: "The mushrooms are definitely magical in nature. They seem to be responding to the ambient magical energy in the forest, possibly serving as indicators of places where the veil between worlds is thin.",
          rewards: {
            experience: 30
          },
          failure: "While the mushrooms glow, you can't determine if this is due to magical properties or simply bioluminescence natural to the species."
        }
      }
    ],
    choices: [
      {
        id: 'continue_forward',
        text: "Continue deeper into the forest",
        nextScene: 'mysterious_clearing',
        requires: null
      },
      {
        id: 'collect_mushrooms',
        text: "Carefully collect some mushrooms",
        nextScene: 'collect_glowing_mushrooms',
        requires: null
      },
      {
        id: 'stop_listen',
        text: "Stop and listen carefully",
        nextScene: 'forest_sounds',
        requires: null
      }
    ]
  },
  
  collect_glowing_mushrooms: {
    id: 'collect_glowing_mushrooms',
    title: "Collecting Mushrooms",
    description: `You carefully attempt to gather some of the glowing mushrooms, thinking they might be useful later.`,
    skillChecks: [
      {
        skill: 'sleightOfHand',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Harvest the delicate mushrooms without damaging them",
        results: {
          success: "With gentle, precise movements, you successfully collect several intact mushrooms. They continue to glow softly in your pack.",
          rewards: {
            items: ['elixir_of_clarity'],
            experience: 25
          },
          failure: "The mushrooms are more fragile than you expected. They crumble at your touch, releasing a cloud of glowing spores that make you cough and your eyes water."
        }
      }
    ],
    choices: [
      {
        id: 'continue_forward',
        text: "Continue deeper into the forest",
        nextScene: 'mysterious_clearing',
        requires: null
      },
      {
        id: 'stop_listen',
        text: "Stop and listen carefully",
        nextScene: 'forest_sounds',
        requires: null
      }
    ]
  },
  
  forest_sounds: {
    id: 'forest_sounds',
    title: "Forest Whispers",
    description: `You stop on the path and stand completely still, focusing on the sounds of the forest around you. At first, you hear only the usual forest ambiance - the rustling of leaves, distant bird calls, the occasional scurrying of small animals in the underbrush.

But as you concentrate, other sounds emerge: a faint, rhythmic drumming from somewhere deep in the forest; what sounds like distant, melodic singing or chanting in a language you don't recognize; and closer by, something large moving carefully through the forest, trying to stay quiet but occasionally snapping twigs under its weight.`,
    skillChecks: [
      {
        skill: 'perception',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Pinpoint the direction of the sounds",
        results: {
          success: "The drumming and singing are coming from somewhere ahead and to the right of the path. The large creature seems to be paralleling your route, moving through the forest about 50 yards to your left, in the same direction you've been traveling.",
          rewards: {
            experience: 20
          },
          failure: "While you can hear the sounds, the forest's acoustics make it difficult to determine exactly which direction they're coming from."
        }
      },
      {
        skill: 'insight',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Interpret the meaning of these sounds",
        results: {
          success: "The rhythmic nature of the drums and chanting suggests some kind of ritual or ceremony rather than random forest sounds. The creature paralleling your path is likely tracking you but not yet hostile - possibly curious or cautious.",
          rewards: {
            experience: 30
          },
          failure: "The sounds are unusual but could have many explanations - both natural and supernatural. Without more information, you can't determine their significance."
        }
      }
    ],
    choices: [
      {
        id: 'continue_forward',
        text: "Continue along the path",
        nextScene: 'mysterious_clearing',
        requires: null
      },
      {
        id: 'investigate_creature',
        text: "Try to spot the creature moving nearby",
        nextScene: 'forest_creature',
        requires: null
      },
      {
        id: 'follow_sounds',
        text: "Move toward the drumming and chanting",
        nextScene: 'approach_ritual',
        requires: null
      }
    ]
  },
  
  forest_creature: {
    id: 'forest_creature',
    title: "Forest Stalker",
    description: `You carefully move off the path, trying to catch a glimpse of whatever has been moving alongside you through the forest. The undergrowth is thick, making it difficult to move quietly or see very far.`,
    skillChecks: [
      {
        skill: 'stealth',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Move quietly through the forest",
        results: {
          success: "Moving with exceptional care, you manage to approach without alerting the creature. Through a gap in the foliage, you catch sight of a large, shaggy figure with antlers - some kind of forest guardian or spirit, watching your progress with intelligent eyes.",
          rewards: {
            experience: 40
          },
          failure: "Despite your efforts to move quietly, you step on a dry branch that snaps loudly. You hear a startled sound and catch only a glimpse of a large, swift shape bounding away through the forest, too quick to identify clearly."
        }
      },
      {
        skill: 'animal_handling',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Attempt to appear non-threatening",
        results: {
          success: "You adopt a non-threatening posture and movement pattern. After a tense moment, a magnificent stag with unusually intelligent eyes steps into view. It regards you calmly for a long moment, then deliberately turns and walks a short distance before looking back at you - as if it wants you to follow.",
          nextScene: 'stag_guide',
          rewards: {
            experience: 35
          },
          failure: "Your attempt to appear friendly doesn't work as intended. Whatever was following you remains hidden, though the sounds of movement fade as it apparently decides to keep its distance."
        }
      }
    ],
    choices: [
      {
        id: 'return_to_path',
        text: "Return to the main path",
        nextScene: 'mysterious_clearing',
        requires: null
      },
      {
        id: 'follow_sounds',
        text: "Move toward the drumming and chanting instead",
        nextScene: 'approach_ritual',
        requires: null
      }
    ]
  },
  
  forest_ridge: {
    id: 'forest_ridge',
    title: "Forest Ridge",
    description: `The right-hand path climbs steadily up a wooded ridge. As you ascend, the forest changes subtly - the trees are more widely spaced, allowing more sunlight to filter through. Different plants grow here, including flowering bushes with bright red berries and patches of tall ferns.

The path is less distinct, occasionally disappearing entirely before becoming visible again. Looking back, you can see glimpses of the forest below through gaps in the trees, giving you a better sense of the landscape.

Near the top of the ridge, you come across a massive fallen tree blocking the path. Its trunk is as wide as you are tall, and its tangled branches create a significant obstacle.`,
    choices: [
      {
        id: 'climb_over',
        text: "Climb over the fallen tree",
        nextScene: 'ridge_top',
        requires: null
      },
      {
        id: 'go_around',
        text: "Look for a way around the tree",
        nextScene: 'ridge_detour',
        requires: null
      },
      {
        id: 'examine_tree',
        text: "Examine the fallen tree",
        nextScene: 'fallen_tree_examination',
        requires: null
      }
    ],
    encounters: ['fallen_tree']
  }
};